/**
 * AdminMenu/GetMenu 的契約:`system_name` ↔ 前端模組鍵。
 *
 * 為什麼獨立成一支純模組:這份對照表原本住在 `query/useAdminMenu.js`(帶 react-query),
 * 但 RBAC 也要用它 —— 8/14 實測發現 `DcCustomerRole/GetById` 的 modules 可能整批
 * 回 Hidden,而 GetMenu(swagger 明寫「依目前登入者權限過濾」)卻回整份可見選單。
 * 兩個後端來源互相矛盾時,RBAC 需要讀得到選單這一邊。
 *
 * 純資料 + 純函式,不 import 任何 React/query,避免 RbacContext ← useAdminMenu 的相依。
 */

/**
 * GetMenu 的快取設定。放在這支純模組(不帶 react-query)是因為兩個消費者都要:
 * 側欄 `useAdminMenu` 與 `sessionStore.assembleIdentity()` 用**同一把 key** 共用快取
 * (各自寫死的話 key 一旦漂掉就變回「每次登入打兩次」,8/18 修掉的那個),
 * 而 sessionStore ↔ useAdminMenu 互相 import 會成循環相依。
 */
export const ADMIN_MENU_QUERY_KEY = ["adminMenu"];
export const ADMIN_MENU_STALE_TIME = 5 * 60 * 1000; // 選單變動頻率低

/** 真後端 GetMenu 的 system_name(8/11 實回)→ 前端模組鍵 */
export const SYSTEM_ALIASES = {
  PeopleManagement: "People",
  RoomManagement: "Room",
  OperationInfo: "Registry",
  AssetManagement: "Asset",
  MonitoringDispatch: "Monitor",
  MessageCenter: "Notify",
  Reporting: "Report",
  DecisionAnalysis: "Decision",
};

/**
 * 選單樹 → `Set<system_name>`(含 alias 後的前端鍵)。
 * `visible === false` 的節點不算;子節點一併走訪(子項如 PermissionSetting 沒有 alias,
 * 以原名進集合,不影響過濾)。
 */
export function flattenMenuSystems(items, acc = new Set()) {
  (items || []).forEach((m) => {
    if (m.visible !== false && m.system_name) {
      acc.add(m.system_name);
      if (SYSTEM_ALIASES[m.system_name]) acc.add(SYSTEM_ALIASES[m.system_name]);
    }
    flattenMenuSystems(m.child_nodes, acc);
  });
  return acc;
}

/** 選單樹 → 前端模組鍵陣列(只留對照得到的,供 RBAC 換算能力) */
export function menuModuleKeys(items) {
  const known = new Set(Object.values(SYSTEM_ALIASES).concat("Dashboard"));
  return [...flattenMenuSystems(items)].filter((s) => known.has(s));
}
